import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import TablePagination from "@material-ui/core/TablePagination";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import useStyles from "./useStyles";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import { ConfirmDialog, FormDialog } from "../Dialogs";
import { Typography } from "@material-ui/core";
import { hasAttributes } from "utils/objects";
import Icon from "components/common_components/Icon";
import { Table, TableHead, TableBody, TableRow, TableCell, TableHeaderCell } from "../Table";
import { hasElements } from "utils/arrays";
import Money from "../Money";
import { dateToString } from "utils/dateUtils";

function EnhancedTable({
    headCells,
    data,
    title,
    selectable,
    allowSelectAll,
    allowMultiSelect,
    defaultOrder,
    keyColumnName,
    onRowClick,
    onRowDoubleClick,
    onDelete,
    onSelectedChanged,
    deletable,
    filterable,
    pageable,
    orderable,
    onRequestData,
    pageableData,
    filterBuilder,
    defaultRowsPerPage,
    renderBulkActions,
    advancedFiltersProps,
    getRowIdentifier,
    presetFilterValue,
    onFilterChange,
}) {
    const classes = useStyles();
    const [order, setOrder] = useState(defaultOrder.order);
    const [orderBy, setOrderBy] = useState(defaultOrder.orderBy);
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [filterValue, setFilterValue] = useState(presetFilterValue || "");
    const [deleteDialogProps, setDeleteDialogProps] = useState({
        open: false,
        title: `Delete ${title}`,
        content: "Do you want to proceed?",
        onClose: () => setDeleteDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        onSecondaryClick: undefined,
    });
    const [advancedFiltersDialogProps, setAdvancedFiltersDialogProps] = useState({
        open: false,
        title: "Advanced filters",
        formId: "adv-filters-dialog-form",
        inFullScreen: false,
        maxWidth: "md",
        primaryButtonText: "Apply",
        onClose: () => {
            setAdvancedFiltersDialogProps((prev) => ({
                ...prev,
                open: false,
            }));
        },
    });

    const {
        context: AdvancedFiltersContext,
        component: AdvancedFiltersComponent,
        normalizers: AdvancedFiltersNormalizers,
    } = advancedFiltersProps;
    const withAdvancedFilters = Boolean(AdvancedFiltersComponent) && Boolean(AdvancedFiltersContext) && Boolean(AdvancedFiltersNormalizers);
    const { advancedFilters, setAdvancedFilters } = useContext(withAdvancedFilters ? AdvancedFiltersContext : React.createContext({}));

    function buildFilters(quickSearchValue = filterValue) {
        return hasAttributes(advancedFilters)
            ? { ...advancedFilters, isQuickSearch: false, quickSearchValue: null }
            : filterBuilder(quickSearchValue);
    }

    const { totalElements } = pageableData;

    function setSelectedRows(selectedRows) {
        setSelected(selectedRows);
        onSelectedChanged(selectedRows);
    }

    function getOrderby(property) {
        const headCell = headCells.find((x) => x.id === property);
        return headCell ? headCell.orderBy || property : property;
    }

    const handleRequestSort = (event, property) => {
        if (!orderable) return;
        const isAsc = orderBy === property && order === "asc";
        const newOrder = isAsc ? "desc" : "asc";
        setOrder(newOrder);
        setOrderBy(property);
        onRequestData(
            {
                filter: buildFilters(),
                orderBy: getOrderby(property),
                orderDirection: newOrder,
                pageNumber: page,
                pageSize: rowsPerPage,
            },
            false,
        );
    };

    const handleSelectAllClick = (event) => {
        if (allowMultiSelect && allowSelectAll) {
            if (event.target.checked) {
                const newSelected = data.map((n) => getRowIdentifierValue(n));
                setSelectedRows(newSelected);
                return;
            }
        }
        setSelectedRows([]);
    };

    const handleClick = (event, name) => {
        if (allowMultiSelect) {
            const selectedIndex = selected.indexOf(name);
            let newSelected = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, name);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
            }

            setSelectedRows(newSelected);
        } else {
            setSelectedRows([name]);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        onRequestData(
            {
                filter: buildFilters(),
                orderBy: getOrderby(orderBy),
                orderDirection: order,
                pageNumber: newPage,
                pageSize: rowsPerPage,
            },
            false,
        );
    };

    const handleChangeRowsPerPage = (event) => {
        const pageSize = parseInt(event.target.value, 10);
        setRowsPerPage(pageSize);
        setPage(0);
        onRequestData(
            {
                filter: buildFilters(),
                orderBy: getOrderby(orderBy),
                orderDirection: order,
                pageNumber: 0,
                pageSize,
            },
            false,
        );
    };

    function onFilterHandler(value) {
        const fValue = (value || "").trim().toLowerCase();
        setFilterValue(fValue);
        if (onFilterChange) onFilterChange(fValue);
        setPage(0);
        onRequestData(
            {
                filter: buildFilters(fValue),
                orderBy: getOrderby(orderBy),
                orderDirection: order,
                pageNumber: 0,
                pageSize: rowsPerPage,
            },
            false,
        );
    }

    function onResetAdvancedFilters() {
        setAdvancedFilters(undefined);
        setPage(0);
        onRequestData(
            {
                filter: { isQuickSearch: false, quickSearchValue: null },
                orderBy: getOrderby(orderBy),
                orderDirection: order,
                pageNumber: 0,
                pageSize: rowsPerPage,
            },
            false,
        );
    }

    function onResetSingleFilter(advancedFilterKey) {
        delete advancedFilters[advancedFilterKey];
        const newAdvancedfilters = { ...advancedFilters };
        setAdvancedFilters(newAdvancedfilters);
        setPage(0);
        onRequestData(
            {
                filter: { ...newAdvancedfilters, isQuickSearch: false, quickSearchValue: null },
                orderBy: getOrderby(orderBy),
                orderDirection: order,
                pageNumber: 0,
                pageSize: rowsPerPage,
            },
            false,
        );
    }

    function onAdvancedFiltersApply(values, { setSubmitting }) {
        const normalizedFilters = hasAttributes(values)
            ? Object.keys(values).reduce((map, key) => {
                  const val = values[key];
                  const isValueValid = typeof val !== "undefined" && Boolean(val) && val.toString().trim().length > 0;
                  return isValueValid ? { ...map, [key]: values[key] } : map;
              }, {})
            : {};
        setAdvancedFilters(normalizedFilters);
        setFilterValue(undefined);
        setPage(0);
        onRequestData(
            {
                filter: { ...normalizedFilters, isQuickSearch: false, quickSearchValue: null },
                orderBy: getOrderby(orderBy),
                orderDirection: order,
                pageNumber: 0,
                pageSize: rowsPerPage,
            },
            false,
        );
        advancedFiltersDialogProps.onClose();
        setSubmitting && typeof setSubmitting === "function" && setSubmitting(false);
    }

    function onRowClickHandler(event, row) {
        if (event.target.type === "checkbox") {
            event.preventDefault();
        } else {
            onRowClick(row);
        }
    }

    function onRowDoubleClickHandler(event, row) {
        if (event.target.type === "checkbox") {
            event.preventDefault();
        } else {
            onRowDoubleClick(row);
        }
    }

    function onDeleteHandler() {
        if (selected && selected.length > 0) {
            const gramaticalNumber = selected.length === 1 ? "element" : "elements";
            setDeleteDialogProps((prev) => ({
                ...prev,
                open: true,
                content: `Are you sure you want to delete ${selected.length} ${gramaticalNumber} from the list?`,
                onPrimaryClick: () => {
                    onDelete(selected);
                    setSelectedRows([]);
                    prev.onClose();
                },
                onSecondaryClick: prev.onClose,
            }));
        }
    }

    function renderCell2(type, value, index, labelId) {
        switch (type) {
            case "date":
                return (
                    <TableCell key={index} type="text">
                        {dateToString(value, false)}
                    </TableCell>
                );
            case "datetime":
                return (
                    <TableCell key={index} type="text">
                        {dateToString(value, true)}
                    </TableCell>
                );
            case "bool":
                return (
                    <TableCell key={index} type={type}>
                        <Checkbox color="primary" checked={value} inputProps={{ "aria-labelledby": labelId }} />
                    </TableCell>
                );
            case "currency":
                return (
                    <TableCell key={index} type={type}>
                        <Typography component="h6" variant="body2" align="right">
                            <strong>
                                <Money amount={value} />
                            </strong>
                        </Typography>
                    </TableCell>
                );
            default:
                return (
                    <TableCell key={index} type="text">
                        {value}
                    </TableCell>
                );
        }
    }

    const getRowIdentifierValue = (row) => {
        if (getRowIdentifier && typeof getRowIdentifier === "function") {
            return getRowIdentifier(row);
        }

        const value = row[keyColumnName];
        return value;
    };

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                {(hasElements(selected) || filterable || deletable) && (
                    <EnhancedTableToolbar
                        selected={selected}
                        title={title}
                        onDelete={onDeleteHandler}
                        onFilter={onFilterHandler}
                        filterValue={filterValue}
                        setFilterValue={setFilterValue}
                        deletable={deletable}
                        filterable={filterable}
                        allowMultiSelect={allowMultiSelect}
                        renderBulkActions={renderBulkActions}
                        clearSelection={() => setSelectedRows([])}
                        advancedFiltersProps={{
                            withAdvancedFilters,
                            advancedFilters,
                            onResetAdvancedFilters,
                            setAdvancedFiltersDialogProps,
                            onResetSingleFilter,
                            AdvancedFiltersNormalizers,
                        }}
                    />
                )}
                {withAdvancedFilters && (
                    <FormDialog {...advancedFiltersDialogProps}>
                        {advancedFiltersDialogProps.open && (
                            <AdvancedFiltersComponent advancedFilters={advancedFilters} onSubmitHandler={onAdvancedFiltersApply} />
                        )}
                    </FormDialog>
                )}
                <Table>
                    <TableHead>
                        <TableHeaderCell className="table__cell">
                            {selectable && allowMultiSelect && allowSelectAll && (
                                <Checkbox
                                    color="primary"
                                    indeterminate={selected.length > 0 && selected.length < data.length}
                                    checked={data.length > 0 && selected.length === data.length}
                                    onChange={handleSelectAllClick}
                                    inputProps={{ "aria-label": "select all" }}
                                />
                            )}
                        </TableHeaderCell>
                        {headCells.map((headCell) => (
                            <TableHeaderCell key={headCell.id}>
                                <div
                                    className="table__filter"
                                    onClick={(e) => headCell.sortable !== false && handleRequestSort(e, headCell.id)}
                                >
                                    <span>{headCell.label}</span>
                                    {orderable && orderBy === headCell.id ? (
                                        <Icon name={order === "desc" ? "caret-down" : "caret-up"} size="xs" color="primary" />
                                    ) : null}
                                </div>
                            </TableHeaderCell>
                        ))}
                    </TableHead>
                    <TableBody>
                        {data.map((row, index) => {
                            const isRowChecked = selected.indexOf(getRowIdentifierValue(row)) !== -1;
                            const labelId = `table-checkbox-${index}`;
                            const cells = headCells.map((hc) => hc.id);
                            let key = getRowIdentifierValue(row);
                            if (typeof key === "object") {
                                key = JSON.stringify(key);
                            }

                            return (
                                <TableRow
                                    key={key}
                                    index={index}
                                    checkable={selectable}
                                    checked={isRowChecked}
                                    onCheckedChange={(event) => handleClick(event, getRowIdentifierValue(row))}
                                    onClick={(event) => onRowClickHandler(event, row)}
                                    onDoubleClick={(event) => onRowDoubleClickHandler(event, row)}
                                >
                                    {cells.map((cell) => {
                                        const { index, render, type } = headCells.find((hc) => hc.id === cell);
                                        const splittedCellName = cell.toString().split(".");
                                        const value =
                                            splittedCellName && Array.isArray(splittedCellName)
                                                ? splittedCellName.reduce((val, objName) => (val ? val[objName] : null), row)
                                                : row[cell];

                                        return render ? (
                                            <TableCell key={index}>{render(cell, { value, index, data: row })}</TableCell>
                                        ) : (
                                            renderCell2(type, value, index, labelId)
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {pageable && (
                    <div className="table-pagination">
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                            component="div"
                            count={totalElements}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </div>
                )}
            </Paper>
            <ConfirmDialog {...deleteDialogProps} />
        </div>
    );
}

EnhancedTable.propTypes = {
    headCells: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            index: PropTypes.number,
            numeric: PropTypes.bool,
            label: PropTypes.string,
            orderBy: PropTypes.string,
            sortable: PropTypes.bool,
            render: PropTypes.func,
            type: PropTypes.oneOf(["text", "bool", "currency", "date", "datetime"]),
        }),
    ),
    data: PropTypes.arrayOf(PropTypes.shape({})),
    title: PropTypes.string,
    selectable: PropTypes.bool,
    allowSelectAll: PropTypes.bool,
    allowMultiSelect: PropTypes.bool,
    defaultOrder: PropTypes.shape({
        order: PropTypes.string,
        orderBy: PropTypes.string,
    }),
    keyColumnName: PropTypes.string.isRequired,
    onRowClick: PropTypes.func,
    onRowDoubleClick: PropTypes.func,
    onDelete: PropTypes.func,
    onFilter: PropTypes.func,
    onSelectedChanged: PropTypes.func,
    deletable: PropTypes.bool,
    filterable: PropTypes.bool,
    pageable: PropTypes.bool,
    orderable: PropTypes.bool,
    onRequestData: PropTypes.func,
    pageableData: PropTypes.shape({
        totalElements: PropTypes.number,
    }),
    filterBuilder: PropTypes.func,
    defaultRowsPerPage: PropTypes.number,
    renderBulkActions: PropTypes.func,
    advancedFiltersProps: PropTypes.shape({
        context: PropTypes.shape({}),
        component: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        normalizers: PropTypes.shape({
            keyNames: PropTypes.shape({}),
            valueNormalizers: PropTypes.shape({}),
        }),
    }),
    getRowIdentifier: PropTypes.func,
    presetFilterValue: PropTypes.string,
    onFilterChange: PropTypes.func,
};

EnhancedTable.defaultProps = {
    title: "",
    data: [],
    selectable: false,
    allowSelectAll: true,
    allowMultiSelect: true,
    defaultOrder: {
        order: undefined,
        orderBy: undefined,
    },
    onRowClick: () => {},
    onRowDoubleClick: () => {},
    onDelete: () => {},
    onFilter: () => {},
    onSelectedChanged: () => {},
    deletable: true,
    filterable: true,
    pageable: true,
    orderable: true,
    onRequestData: () => [],
    pageableData: {
        totalElements: 0,
    },
    filterBuilder: () => ({}),
    defaultRowsPerPage: 5,
    renderBulkActions: () => {},
    advancedFiltersProps: {
        context: undefined,
        component: undefined,
        normalizers: undefined,
    },
    getRowIdentifier: undefined,
    presetFilterValue: undefined,
    onFilterChange: undefined,
};

export default EnhancedTable;
